import { z } from "zod";

import { PrincipalType } from "@/shared/principal";

/**
 * Client twin of the backend's `roboto.query.filters`: the filter wire model —
 * row variants, comparator/preset vocabulary, and the `filterSchema` validator.
 * The backend is the authoritative owner; `filtersParity.test.ts` checks this
 * module against the manifest the Python models generate. UI-side policy (which
 * operators a field OFFERS) lives with `FilterDefinition` in
 * `components/filters`, not here.
 */

export type FilterDataType =
  | "string"
  | "numeric"
  | "date"
  | "boolean"
  | "set"
  | "enum"
  // An identity-valued field (`created_by` / `modified_by`). Its value is a
  // fully-qualified principal string (`user:<id>` / `device:<id>@<org>` /
  // `invocation:<id>`), and the principal TYPE is the operator: `IS_USER` picks
  // specific users, `IS_ANY_USER` matches every one of them.
  | "identity"
  // A user-created metric (dot-delimited path). Renders like `numeric` in the UI
  // (same comparators, value input, chip), but its `field` is a BARE path that
  // `filterToCondition` rewrites to `metric.<field>` — that prefixing is why the
  // variant exists. It's also distinct so a metric is distinguishable from a
  // numeric property (e.g. click-to-edit opens the metrics picker, not the form).
  | "metric";

/**
 * How distinct property filters join in the wire condition. `"AND"` (the
 * default) requires every filter to match; `"OR"` requires at least one.
 *
 * Lives in the filter schema (alongside `Filter`) rather than on the table
 * primitive because it's a filter concept, not a table concept.
 */
export type FilterMatchMode = "AND" | "OR";

/**
 * The list comparators: "is any of" and "is none of". The only comparators
 * that take several values of equality (`EQUALS` / `NOT_EQUALS` take exactly
 * one), and the only ones that can carry the "Not set" member
 * (`includeNull`). They exist only in UI state: the query language has no list
 * comparator, so each is sent as the condition group it means
 * (`listToCondition`), and saved as the equality row it means
 * (`savedFilters.ts`).
 */
export const listComparators = ["ANY_OF", "NONE_OF"] as const;

export type ListFilterComparator = (typeof listComparators)[number];

/** True iff `comparator` is `ANY_OF` or `NONE_OF`. */
export function isListComparator(
  comparator: FilterComparator,
): comparator is ListFilterComparator {
  return (listComparators as readonly string[]).includes(comparator);
}

// Value-bearing comparators per filter type — the ones that take an operand.
// The presence pair (IS_NOT_NULL / IS_NULL) is omitted here on purpose.
const stringValueComparators = [
  "EQUALS",
  "NOT_EQUALS",
  "CONTAINS",
  "NOT_CONTAINS",
  "LIKE",
  "NOT_LIKE",
  ...listComparators,
] as const;

const numericValueComparators = [
  "EQUALS",
  "NOT_EQUALS",
  "GREATER_THAN",
  "LESS_THAN",
  "GREATER_THAN_OR_EQUAL",
  "LESS_THAN_OR_EQUAL",
  ...listComparators,
] as const;

const dateValueComparators = ["LESS_THAN", "GREATER_THAN", "BETWEEN"] as const;

// No NONE_OF: "none of" a boolean is "any of" the other value, plus "Not
// set" where the field can be unset.
const booleanValueComparators = ["EQUALS", "ANY_OF"] as const;

// Closed-option-set fields (a genuine enum, and custom Enum fields). The
// picker offers only the lists; EQUALS / NOT_EQUALS stay accepted for stored
// filters and older clients, and are read as the lists (`upgradeLegacyFilter`).
const enumValueComparators = [
  "EQUALS",
  "NOT_EQUALS",
  ...listComparators,
] as const;

// Identity fields: the principal TYPE is the operator, and the two operators a
// type contributes split along the same comparator/preset seam every other
// field type uses.
//
// `IS_<TYPE>` is a value-bearing COMPARATOR: it makes the user pick which
// entities of that type to match, and a multi-select fans out to an OR of
// EQUALS.
const identityValueComparators = [
  "IS_USER",
  "IS_DEVICE",
  "IS_INVOCATION",
  "IS_INTEGRATION",
  "IS_ORG",
] as const;

// `IS_ANY_<TYPE>` is a PRESET: valueless, with the comparator alone carrying
// the predicate, expanded at query-emit time (to a `<type>:%` LIKE prefix in
// `filterToCondition`) exactly like the date relatives. Registering it as one
// rather than hand-listing it in `VALUELESS_COMPARATORS` is what makes it
// valueless, renders it in the picker's preset row, and keeps `isPreset` true
// for it.
export const identityPresets = [
  "IS_ANY_USER",
  "IS_ANY_DEVICE",
  "IS_ANY_INVOCATION",
  "IS_ANY_INTEGRATION",
  "IS_ANY_ORG",
] as const;

// The full identity vocabulary — both halves, for the wire schema. Which of
// these a given field actually offers is decided per-definition by
// `effectiveComparators` / `effectivePresets` from the types it declares, so
// this is never any one field's menu.
//
// No presence axis: `created_by` / `modified_by` are non-null on the tables
// they target, so — like `set` — there is nothing to layer on per-field.
export const identityComparators = [
  ...identityValueComparators,
  ...identityPresets,
] as const;

export type IdentityComparator = (typeof identityComparators)[number];

/** The comparator / preset pair a single principal type contributes. */
export interface IdentityOperatorPair {
  /** The value-bearing `IS_<TYPE>` comparator. */
  comparator: IdentityComparator;
  /** The valueless `IS_ANY_<TYPE>` preset. */
  preset: IdentityComparator;
}

/**
 * The operator pair each principal type contributes. Exhaustive over
 * {@link PrincipalType} so a new platform principal type fails to compile
 * here until it gets operators, rather than silently becoming unfilterable.
 * Exported for the operator-picker policy in `FilterSchema.ts`, which narrows
 * an identity field's menu to the principal types it declares.
 */
export const IDENTITY_OPERATORS_BY_TYPE: Record<
  PrincipalType,
  IdentityOperatorPair
> = {
  user: { comparator: "IS_USER", preset: "IS_ANY_USER" },
  device: {
    comparator: "IS_DEVICE",
    preset: "IS_ANY_DEVICE",
  },
  invocation: {
    comparator: "IS_INVOCATION",
    preset: "IS_ANY_INVOCATION",
  },
  integration: {
    comparator: "IS_INTEGRATION",
    preset: "IS_ANY_INTEGRATION",
  },
  org: { comparator: "IS_ORG", preset: "IS_ANY_ORG" },
};

/** What an identity comparator means, once decoded. */
export interface IdentityOperator {
  /** The principal type the operator selects. */
  type: PrincipalType;
  /**
   * `true` for the `IS_ANY_<TYPE>` preset: matches every principal of `type`
   * via the `<type>:%` LIKE prefix, so the operator carries the whole predicate
   * and takes no value.
   */
  matchesAnyOfType: boolean;
}

// Inverted from `IDENTITY_OPERATORS_BY_TYPE` rather than written out again,
// so the two directions can't disagree.
const IDENTITY_OPERATORS: Record<IdentityComparator, IdentityOperator> = (
  Object.entries(IDENTITY_OPERATORS_BY_TYPE) as [
    PrincipalType,
    IdentityOperatorPair,
  ][]
).reduce(
  (operators, [type, pair]) => {
    operators[pair.comparator] = { type, matchesAnyOfType: false };
    operators[pair.preset] = { type, matchesAnyOfType: true };
    return operators;
  },
  {} as Record<IdentityComparator, IdentityOperator>,
);

/**
 * Decode an identity comparator into the principal type it targets and whether
 * it matches that type wholesale. Returns `undefined` for any other comparator,
 * so callers holding an umbrella {@link FilterComparator} can use it as a guard.
 */
export function identityOperator(
  comparator: FilterComparator,
): IdentityOperator | undefined {
  return IDENTITY_OPERATORS[comparator as IdentityComparator];
}

// Presence checks ("is set" / "is not set") shared by every scalar type that
// has a nullability axis. Valueless — the comparator alone carries the semantic.
// Layered onto a field's set by `effectiveComparators`, only when the field is `nullable`.
// Order is a UI choice (affirmative first).
export const presenceComparators = ["IS_NOT_NULL", "IS_NULL"] as const;

// Full per-type vocabulary: every comparator a type can carry (value-bearing +
// presence).
export const stringComparators = [
  ...stringValueComparators,
  ...presenceComparators,
] as const;

export const numericComparators = [
  ...numericValueComparators,
  ...presenceComparators,
] as const;

export const dateComparators = [
  ...dateValueComparators,
  ...presenceComparators,
] as const;

export const booleanComparators = [
  ...booleanValueComparators,
  ...presenceComparators,
] as const;

export const enumComparators = [
  ...enumValueComparators,
  ...presenceComparators,
] as const;

// Set-membership fields (e.g. tags) have no presence axis — the same
// CONTAINS / NOT_CONTAINS the backend's TAG_FIELD_COMPARATORS uses.
export const setComparators = ["CONTAINS", "NOT_CONTAINS"] as const;

/**
 * Preset comparators are a valueless UI convenience.
 */
export const datePresets = [
  "TODAY",
  "LAST_3_HOURS",
  "LAST_8_HOURS",
  "LAST_24_HOURS",
  "LAST_7_DAYS",
  "LAST_30_DAYS",
  "LAST_90_DAYS",
  "THIS_MONTH",
] as const;

export const comparatorsByType = {
  string: stringComparators,
  numeric: numericComparators,
  date: dateComparators,
  boolean: booleanComparators,
  set: setComparators,
  enum: enumComparators,
  identity: identityValueComparators,
  metric: numericComparators,
} as const satisfies Record<FilterDataType, readonly string[]>;

export const presetsByType = {
  string: [],
  numeric: [],
  date: datePresets,
  boolean: [],
  set: [],
  enum: [],
  identity: identityPresets,
  metric: [],
} as const satisfies Record<FilterDataType, readonly string[]>;

type ComparatorsFor<T extends FilterDataType> =
  | (typeof comparatorsByType)[T][number]
  | (typeof presetsByType)[T][number];

export type StringComparator = ComparatorsFor<"string">;
export type NumericComparator = ComparatorsFor<"numeric">;
export type DateComparator = ComparatorsFor<"date">;
export type BooleanComparator = ComparatorsFor<"boolean">;
export type SetComparator = ComparatorsFor<"set">;
export type EnumComparator = ComparatorsFor<"enum">;

export type FilterComparator = ComparatorsFor<FilterDataType>;

/**
 * Subtype of `DateComparator` corresponding to the relative presets — the
 * narrow form `filterToCondition` accepts when expanding to a live
 * `now`-relative range.
 */
export type DatePresetComparator = (typeof datePresets)[number];

/**
 * All preset comparators across every field type — derived from
 * `presetsByType` so adding/reordering presets only touches one place.
 * Presets are valueless and resolved at query-emit time: date relatives
 * expand to a `now`-relative range inside `filterToCondition` so a saved
 * filter stays live across days, and identity `IS_ANY_<TYPE>` expands to a
 * `<type>:%` LIKE prefix there too.
 */
export const PRESET_COMPARATORS: ReadonlySet<FilterComparator> = new Set(
  Object.values(presetsByType).flat(),
);

/**
 * Comparators that render no value input — the comparator alone carries
 * the semantic: `IS_NULL` / `IS_NOT_NULL` (the presence checks every
 * nullable scalar field can offer), plus every preset.
 */
export const VALUELESS_COMPARATORS: ReadonlySet<FilterComparator> = new Set([
  ...presenceComparators,
  ...PRESET_COMPARATORS,
]);

/** True iff `comparator` is a valueless preset (e.g. date `TODAY`). */
export function isPreset(comparator: FilterComparator): boolean {
  return PRESET_COMPARATORS.has(comparator);
}

/**
 * True iff `comparator` renders no value input — the comparator alone
 * carries the semantic (`IS_NULL` / `IS_NOT_NULL`, plus every preset).
 * Used by FilterForm to decide whether to hide the value input row.
 */
export function isValueless(comparator: FilterComparator): boolean {
  return VALUELESS_COMPARATORS.has(comparator);
}

/**
 * Narrow guard used by `filterToCondition` to dispatch into the
 * date-specific preset→range expander. Distinct from the generic
 * `isPreset` because preset expansion logic is type-specific (today only
 * dates) and needs the narrowed `DatePresetComparator` for exhaustiveness.
 */
export function isDatePreset(
  comparator: FilterComparator,
): comparator is DatePresetComparator {
  return (datePresets as readonly string[]).includes(comparator);
}

// `ANY_OF` / `NONE_OF` only: "Not set" is also a member. On `ANY_OF` the
// filter matches rows with no value for the field as well. `NONE_OF` is the
// negation of `ANY_OF` over the same members, so it keeps rows with no value
// unless "Not set" is a member, which leaves them out. A flag rather than a
// `null` in `values`, so every `values` consumer stays untouched and a real
// value can never collide with it. `listToCondition` sends it as an `IS_NULL`
// leaf in the any-of group, or an `IS_NOT_NULL` leaf in the none-of group;
// without it, none-of adds `IS_NULL` to keep the rows with no value. Saved
// through `or_null` (see `savedFilters.ts`).
const includeNull = z.boolean().optional();

const stringVariant = z.object({
  type: z.literal("string"),
  field: z.string().min(1),
  comparator: z.enum(stringComparators),
  values: z.array(z.string()),
  includeNull,
});

const numericVariant = z.object({
  type: z.literal("numeric"),
  field: z.string().min(1),
  comparator: z.enum(numericComparators),
  values: z.array(z.number()),
  includeNull,
});

const dateVariant = z.object({
  type: z.literal("date"),
  field: z.string().min(1),
  comparator: z.enum([...dateComparators, ...datePresets]),
  values: z.array(z.string()),
});

const booleanVariant = z.object({
  type: z.literal("boolean"),
  field: z.string().min(1),
  comparator: z.enum(booleanComparators),
  values: z.array(z.boolean()),
  includeNull,
});

// Option-backed filters (`set`, `enum`, `identity`) store the SELECTED
// OPTIONS — wire `value` plus the display `label` the user saw when they
// picked it (structurally `FilterDefinition`'s `LabeledOption`). One
// representation of value→label, owned by the committed filter itself: chips
// render `label` directly, and `filterToCondition` projects `value` at the
// wire boundary, so the label never reaches a `Condition`. For plain-string
// option sets, label === value.
const labeledOption = z.object({
  value: z.string(),
  label: z.string(),
});

const setVariant = z.object({
  type: z.literal("set"),
  field: z.string().min(1),
  comparator: z.enum(setComparators),
  values: z.array(labeledOption),
});

const enumVariant = z.object({
  type: z.literal("enum"),
  field: z.string().min(1),
  comparator: z.enum(enumComparators),
  values: z.array(labeledOption),
  includeNull,
});

const identityVariant = z.object({
  type: z.literal("identity"),
  field: z.string().min(1),
  comparator: z.enum(identityComparators),
  // Labeled options like `set`/`enum`, so a picked principal carries its own
  // display name: `value` is the fully-qualified principal string
  // (`user:<id>`, `device:<id>@<org>`) the column stores, `label` the friendly
  // name the directory showed. Empty for the valueless `IS_ANY_<TYPE>` preset,
  // whose `<type>:%` pattern is derived from the operator at wire time.
  values: z.array(labeledOption),
});

// A metric filter is numeric-shaped; `field` holds the BARE dotted metric path
// (e.g. "navigation.path_deviation"). `filterToCondition` prepends the wire
// `metric.` prefix at conversion time.
const metricVariant = z.object({
  type: z.literal("metric"),
  field: z.string().min(1),
  comparator: z.enum(numericComparators),
  values: z.array(z.number()),
  unit: z.string().nullable().optional(),
  includeNull,
});

/**
 * The current filter shape, with no reading of older ones. `EQUALS` and
 * `NOT_EQUALS` take exactly one value here; several values are `ANY_OF` /
 * `NONE_OF`. Read stored filters with {@link filterSchema}, which upgrades
 * the multi-value `EQUALS` saved before that split, and the enum comparators
 * saved before enums became checklists.
 */
export const strictFilterSchema = z
  .discriminatedUnion("type", [
    stringVariant,
    numericVariant,
    dateVariant,
    booleanVariant,
    setVariant,
    enumVariant,
    identityVariant,
    metricVariant,
  ])
  .superRefine((data, ctx) => {
    if (
      "includeNull" in data &&
      data.includeNull === true &&
      !isListComparator(data.comparator)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["includeNull"],
        message: "Only 'is any of' and 'is none of' can include Not set",
      });
      return;
    }
    if (isValueless(data.comparator)) {
      return;
    }
    // "Not set" alone is a complete list: it matches the unset rows (or, on
    // NONE_OF, the set ones), so the list needs no other member.
    const notSetIsMember =
      isListComparator(data.comparator) &&
      "includeNull" in data &&
      data.includeNull === true;
    if (
      (data.comparator === "EQUALS" || data.comparator === "NOT_EQUALS") &&
      data.values.length > 1
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["values"],
        message: "Takes one value; use 'is any of' for several",
      });
      return;
    }
    switch (data.type) {
      case "string":
        if (!notSetIsMember && !data.values.some((v) => v.trim().length > 0)) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Required",
          });
        }
        return;
      case "numeric":
      case "metric":
        if (
          (data.values.length === 0 && !notSetIsMember) ||
          !data.values.every((n) => Number.isFinite(n))
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Required",
          });
        }
        return;
      case "date":
        if (data.comparator === "BETWEEN") {
          if (
            data.values.length !== 2 ||
            data.values.some((v) => v.length === 0)
          ) {
            ctx.addIssue({
              code: "custom",
              path: ["values"],
              message: "Start and end dates required",
            });
          } else if (data.values[0] > data.values[1]) {
            // ISO 8601 strings (UTC midnight) compare lexicographically,
            // so plain `>` is a correct date ordering check here.
            ctx.addIssue({
              code: "custom",
              path: ["values"],
              message: "End date must be on or after start date",
            });
          }
        } else if (data.values.length === 0 || data.values[0].length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Required",
          });
        }
        return;
      case "boolean":
        if (data.values.length === 0 && !notSetIsMember) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Pick true or false",
          });
        }
        return;
      case "set":
      case "enum":
        if (data.values.length === 0 && !notSetIsMember) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Pick at least one value",
          });
        }
        return;
      case "identity":
        if (
          data.values.length === 0 ||
          !data.values.some((option) => option.value.trim().length > 0)
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Pick an identity",
          });
        }
        return;
    }
  });

/** The list a comparator saved before the lists existed now reads as. */
interface LegacyUpgrade {
  comparator: ListFilterComparator;
  /**
   * Whether "Not set" joins the list. A `NOT_EQUALS` was sent as `!=` (an
   * AND of them, for several values), which leaves out the rows with no value
   * for the field, while "is none of" keeps those unless "Not set" is a
   * member.
   */
  includeNull: boolean;
}

/**
 * What a multi-value comparator saved before `ANY_OF` / `NONE_OF` existed
 * now reads as. Identity is absent: it keeps its `IS_<TYPE>` comparator.
 */
const LEGACY_MULTI_VALUE_UPGRADES: Readonly<Record<string, LegacyUpgrade>> = {
  EQUALS: { comparator: "ANY_OF", includeNull: false },
  NOT_EQUALS: { comparator: "NONE_OF", includeNull: true },
};

/**
 * What an enum comparator saved before enums became checklists now reads as,
 * whatever its value count. The presence checks become the list of "Not set"
 * alone: "is not set" is "is any of Not set", and "is set" is "is none of Not
 * set".
 */
const LEGACY_ENUM_UPGRADES: Readonly<
  Record<string, LegacyUpgrade & { notSetOnly: boolean }>
> = {
  EQUALS: { comparator: "ANY_OF", includeNull: false, notSetOnly: false },
  NOT_EQUALS: { comparator: "NONE_OF", includeNull: true, notSetOnly: false },
  IS_NULL: { comparator: "ANY_OF", includeNull: true, notSetOnly: true },
  IS_NOT_NULL: { comparator: "NONE_OF", includeNull: true, notSetOnly: true },
};

/**
 * `filter` under the list comparator `upgrade` names, with "Not set" added
 * when the upgrade calls for it. "Not set" is added only beside a value: a
 * `NOT_EQUALS` with no value was never a valid filter, and "Not set" alone
 * would make it a valid "is set".
 */
function upgradedTo(
  filter: Record<string, unknown>,
  upgrade: LegacyUpgrade,
): Record<string, unknown> {
  const hasValue = Array.isArray(filter.values) && filter.values.length > 0;
  return upgrade.includeNull && hasValue
    ? { ...filter, comparator: upgrade.comparator, includeNull: true }
    : { ...filter, comparator: upgrade.comparator };
}

/**
 * Upgrade one stored filter from before `EQUALS` became single-valued: an
 * `EQUALS` over two or more values becomes `ANY_OF`, and a `NOT_EQUALS`
 * becomes `NONE_OF` with "Not set" as a member, with the same values in the
 * same order (a boolean `EQUALS [false, true]` included). "Not set" keeps the
 * `NOT_EQUALS` meaning: it was sent as an AND of `!=`, which leaves out the
 * rows with no value for the field, and "is none of" keeps those unless "Not
 * set" is a member. An enum filter goes further, since an enum is only ever a
 * checklist now: its `EQUALS` / `NOT_EQUALS` become the list form at any value
 * count, and `IS_NULL` / `IS_NOT_NULL` become the list of "Not set" alone.
 * Anything else, including a single-value filter of another type, is returned
 * as it is, so the upgrade can run any number of times.
 *
 * Takes and returns raw input: it runs before validation, on filters read
 * from saved Views, dashboards, drafts, URLs and table memory.
 */
export function upgradeLegacyFilter(raw: unknown): unknown {
  if (typeof raw !== "object" || raw === null) {
    return raw;
  }
  const filter = raw as Record<string, unknown>;
  if (typeof filter.comparator !== "string") {
    return raw;
  }
  const enumUpgrade =
    filter.type === "enum"
      ? LEGACY_ENUM_UPGRADES[filter.comparator]
      : undefined;
  if (enumUpgrade !== undefined) {
    return enumUpgrade.notSetOnly
      ? {
          ...filter,
          comparator: enumUpgrade.comparator,
          values: [],
          includeNull: true,
        }
      : upgradedTo(filter, enumUpgrade);
  }
  const upgrade = LEGACY_MULTI_VALUE_UPGRADES[filter.comparator];
  if (
    upgrade === undefined ||
    filter.type === "identity" ||
    !Array.isArray(filter.values) ||
    filter.values.length < 2
  ) {
    return raw;
  }
  return upgradedTo(filter, upgrade);
}

/**
 * Validates a filter, reading the multi-value `EQUALS` / `NOT_EQUALS` saved
 * before `ANY_OF` / `NONE_OF` as those (a `NOT_EQUALS` with "Not set" as a
 * member, so it still leaves out rows with no value), and an enum's
 * single-value and presence comparators as its lists (see
 * {@link upgradeLegacyFilter}).
 * Every reader of stored filters parses through here, so none of them sees
 * the old shape; the output is always {@link strictFilterSchema}'s.
 */
export const filterSchema = z.preprocess(
  // Typed as the current shape's input so a form resolver built on this
  // schema keeps its field types; at runtime any stored value comes through.
  (raw: z.input<typeof strictFilterSchema>) => upgradeLegacyFilter(raw),
  strictFilterSchema,
);

/**
 * The canonical UI Filter shape
 */
export type Filter = z.infer<typeof filterSchema>;

// The value-bearing comparators per type.
export const valueComparatorsByType = {
  string: stringValueComparators,
  numeric: numericValueComparators,
  // A metric is numeric-shaped: same value-bearing operators, and the presence pair
  // layered on by `effectiveComparators` like any other scalar type.
  metric: numericValueComparators,
  date: dateValueComparators,
  boolean: booleanValueComparators,
  enum: enumValueComparators,
  set: setComparators,
  identity: identityValueComparators,
} as const satisfies Record<FilterDataType, readonly FilterComparator[]>;

/** The single-value comparator each list comparator generalizes. */
export const LIST_COMPARATOR_BASES: Readonly<
  Record<ListFilterComparator, FilterComparator>
> = {
  ANY_OF: "EQUALS",
  NONE_OF: "NOT_EQUALS",
};

/**
 * A field's catalog comparators plus the list comparators they imply:
 * `ANY_OF` where `EQUALS` is listed and `NONE_OF` where `NOT_EQUALS` is, each
 * only if `filterType` offers it (so never `NONE_OF` on a boolean, and neither
 * on a date). The RoboQL catalog lists only single-value comparators; a list
 * is sent as a group of the comparator it generalizes, so it works wherever
 * the catalog lists that comparator. For checking a hand-written operator
 * allow-list against the generated catalog.
 *
 * Catalog order is kept, with the derived comparators appended; one the
 * catalog already lists is not repeated.
 */
export function withDerivedListComparators(
  catalogComparators: readonly string[],
  filterType: FilterDataType,
): string[] {
  const offered: readonly string[] = valueComparatorsByType[filterType];
  const derived = listComparators.filter(
    (list) =>
      offered.includes(list) &&
      catalogComparators.includes(LIST_COMPARATOR_BASES[list]) &&
      !catalogComparators.includes(list),
  );
  return [...catalogComparators, ...derived];
}
